import { useEffect, useState } from 'react';
import { MIN_PASSWORD, isValidEmail, usernameToAuthEmail } from '@aop/domain';
import { useAuth, useMe } from '../auth/AuthContext';
import { errMsg } from '../lib/data';
import { supabase } from '../lib/supabase';
import { Dialog } from '../ui/Dialog';
import { PasswordInput } from '../ui/PasswordInput';
import { useToast } from '../ui/Toast';

/** "Tài khoản của tôi": sửa thông tin cá nhân và đổi mật khẩu. */
export function AccountDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const me = useMe();
  const { reloadMe } = useAuth();
  const toast = useToast();
  const [f, setF] = useState({ fullName: '', email: '', phone: '', title: '' });
  const [pw, setPw] = useState({ cur: '', next: '', again: '' });
  const [err, setErr] = useState(''), [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setF({ fullName: me.fullName, email: me.email, phone: me.phone, title: me.title }); setPw({ cur: '', next: '', again: '' }); setErr(''); }
  }, [open, me]);

  const saveInfo = async () => {
    if (!f.fullName.trim() || !f.email.trim()) return setErr('Họ tên và email không được để trống.');
    if (!isValidEmail(f.email.trim())) return setErr('Email không hợp lệ.');
    setBusy(true); setErr('');
    const { error } = await supabase().from('profiles').update({ full_name: f.fullName.trim(), email: f.email.trim(), phone: f.phone.trim(), title: f.title.trim() }).eq('id', me.id);
    setBusy(false);
    if (error) return setErr(errMsg(error, 'Không lưu được, thử lại.'));
    await reloadMe(); toast('Đã lưu thông tin cá nhân');
  };

  const savePassword = async () => {
    if (pw.next.length < MIN_PASSWORD) return setErr(`Mật khẩu mới tối thiểu ${MIN_PASSWORD} ký tự.`);
    if (pw.next !== pw.again) return setErr('Hai lần nhập mật khẩu mới không khớp.');
    setBusy(true); setErr('');
    const check = await supabase().auth.signInWithPassword({ email: usernameToAuthEmail(me.username), password: pw.cur });
    if (check.error) { setBusy(false); return setErr('Mật khẩu hiện tại không đúng.'); }
    const { error } = await supabase().auth.updateUser({ password: pw.next });
    setBusy(false);
    if (error) return setErr(/same|different/i.test(error.message) ? 'Mật khẩu mới phải khác mật khẩu hiện tại.' : 'Không lưu được, thử lại.');
    setPw({ cur: '', next: '', again: '' }); toast('Đã đổi mật khẩu');
  };

  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF(s => ({ ...s, [k]: e.target.value }));
  const setP = (k: keyof typeof pw) => (e: React.ChangeEvent<HTMLInputElement>) => setPw(s => ({ ...s, [k]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} title="Tài khoản của tôi">
      <h4 className="dlg-sec">Thông tin cá nhân</h4>
      <div className="f"><label htmlFor="mUser">Tên đăng nhập</label><input id="mUser" value={me.username} readOnly /></div>
      <div className="f"><label htmlFor="mName">Họ tên</label><input id="mName" value={f.fullName} onChange={set('fullName')} /></div>
      <div className="grid2">
        <div className="f"><label htmlFor="mEmail">Email</label><input id="mEmail" type="email" value={f.email} onChange={set('email')} /></div>
        <div className="f"><label htmlFor="mPhone">Điện thoại</label><input id="mPhone" value={f.phone} onChange={set('phone')} /></div>
      </div>
      <div className="f"><label htmlFor="mTitle">Chức vụ / Bộ phận</label><input id="mTitle" value={f.title} onChange={set('title')} /></div>
      <div><button type="button" className="btn primary" disabled={busy} onClick={() => void saveInfo()}>Lưu thông tin</button></div>

      <h4 className="dlg-sec">Đổi mật khẩu</h4>
      <div className="f"><label htmlFor="mPw0">Mật khẩu hiện tại</label><PasswordInput id="mPw0" autoComplete="current-password" value={pw.cur} onChange={setP('cur')} /></div>
      <div className="grid2">
        <div className="f"><label htmlFor="mPw1">Mật khẩu mới</label><PasswordInput id="mPw1" autoComplete="new-password" value={pw.next} onChange={setP('next')} /></div>
        <div className="f"><label htmlFor="mPw2">Nhập lại mật khẩu mới</label><PasswordInput id="mPw2" autoComplete="new-password" value={pw.again} onChange={setP('again')} /></div>
      </div>
      <div><button type="button" className="btn" disabled={busy} onClick={() => void savePassword()}>Đổi mật khẩu</button></div>
      <p className="auth-err" role="alert">{err}</p>
    </Dialog>
  );
}
